import type { HttpTransport } from '@mangovoice/api-client'
import type {
  CreateVoipProfileInput,
  PaginatedApiResult,
  VoipProfile,
  VoipProfilesQueryParams,
  VoipProfileUacSettings,
} from '@mangovoice/types'

import { stringifyParams } from './utils'

export function createVoipProfilesResource(transport: HttpTransport) {
  return {
    list: (params?: VoipProfilesQueryParams) => {
      const queryString = stringifyParams(
        (params ?? {}) as Record<string, unknown>,
      )
      return transport.get<PaginatedApiResult<VoipProfile>>(
        `/app/v1/voip-profiles/?${queryString}`,
      )
    },

    /**
     * Create a profile (AI-134). Idempotent on the subject pair: a repeat POST
     * returns the existing profile with 200-ish semantics rather than a
     * duplicate or a 400, which is what makes a retried Activate safe.
     */
    create: (data: CreateVoipProfileInput) =>
      transport.post<VoipProfile>('/app/v1/voip-profiles/', data),

    /**
     * Delete a profile. Backend contract (GOV-196): fails if an extension
     * still points at the profile — delete the extension line first.
     */
    delete: (profileId: number) =>
      transport.delete<void>(`/app/v1/voip-profiles/${profileId}/`),

    /**
     * The SIP credentials for the line assigned to this profile, keyed by the
     * profile's subject id (the ai-configuration agent id). Internal route, and
     * staff/superuser only — deliberately, per the 2026-08-17 SIP review.
     *
     * The response carries the line's SIP secret. Do not log it.
     */
    getUacSettings: (pbxId: number, subjectId: string) =>
      transport.get<VoipProfileUacSettings>(
        `/internal/v1/pbxes/${pbxId}/voip-profile-uac-settings/?${stringifyParams(
          { subject_id: subjectId },
        )}`,
      ),
  }
}
